import { useEffect, useState, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import Navigation from '../../components/feature/Navigation';
import Footer from '../home/components/Footer';
import SEOHead from '../../components/SEOHead';


const treatments = [
  { id: 'dental-implants', icon: 'ri-syringe-line', title: 'Dental Implants', tag: 'Most Popular', stat: '98% success rate', duration: '3–6 months' },
  { id: 'porcelain-veneers', icon: 'ri-contrast-2-line', title: 'Porcelain Veneers', tag: '', stat: '10–15 yr lifespan', duration: '2–3 visits' },
  { id: 'smile-makeover', icon: 'ri-star-smile-line', title: 'Complete Smile Makeover', tag: 'Transformative', stat: 'Digital preview included', duration: '1–2 weeks' },
  { id: 'all-on-4', icon: 'ri-grid-line', title: 'All-on-4 Implants', tag: 'Life-Changing', stat: 'Same-day teeth possible', duration: '1 week' },
  { id: 'orthodontics', icon: 'ri-align-center', title: 'Orthodontics', tag: '', stat: 'Clear aligners available', duration: '12–24 months' },
  { id: 'teeth-whitening', icon: 'ri-flashlight-line', title: 'Teeth Whitening', tag: 'Quick', stat: 'Up to 8 shades lighter', duration: '1 hour' },
  { id: 'crowns-bridges', icon: 'ri-building-line', title: 'Crowns & Bridges', tag: '', stat: 'Zirconia or porcelain', duration: '2–3 visits' },
  { id: 'root-canal', icon: 'ri-first-aid-kit-line', title: 'Root Canal Treatment', tag: '', stat: 'Pain-free procedure', duration: '1–2 visits' },
];

const whyPoints = [
  { icon: 'ri-shield-check-line', headline: 'Doctor-supervised. Every step.' },
  { icon: 'ri-money-dollar-circle-line', headline: 'Save up to 70% vs. UK & US prices.' },
  { icon: 'ri-hospital-line', headline: 'JCI-accredited clinics only.' },
  { icon: 'ri-translate-2', headline: 'Full support in your language.' },
];

export default function TreatmentsPage() {
  const { t } = useTranslation();
  const siteUrl = import.meta.env.VITE_SITE_URL || '';
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => setIsVisible(true), 80);
    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    const hash = window.location.hash.replace('#', '');
    if (hash) {
      setTimeout(() => {
        const el = document.getElementById(hash);
        if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }, 300);
    }
  }, []);

  const schema = {
    '@context': 'https://schema.org',
    '@graph': [
      {
        '@type': 'WebPage',
        '@id': `${siteUrl}/treatments`,
        url: `${siteUrl}/treatments`,
        name: 'Dental Treatments in Turkey — Plexus Alliance',
        description: 'Browse all dental treatments available in Turkey: implants, veneers, All-on-4, smile makeovers, orthodontics, whitening & more. Transparent pricing, doctor supervision, JCI-accredited clinics.',
        breadcrumb: {
          '@type': 'BreadcrumbList',
          itemListElement: [
            { '@type': 'ListItem', position: 1, name: 'Home', item: siteUrl },
            { '@type': 'ListItem', position: 2, name: 'Treatments', item: `${siteUrl}/treatments` },
          ],
        },
      },
      {
        '@type': 'ItemList',
        name: 'Dental Treatments Available in Turkey',
        itemListElement: treatments.map((tr, i) => ({
          '@type': 'ListItem',
          position: i + 1,
          item: {
            '@type': 'MedicalProcedure',
            name: tr.title,
            procedureType: 'https://schema.org/SurgicalProcedure',
            status: 'https://schema.org/ActiveActionStatus',
            url: `${siteUrl}/treatments#${tr.id}`,
          },
        })),
      },
    ],
  };

  return (
    <div className="min-h-screen bg-[#0a0a0a]">
      <SEOHead
        title="All Dental Treatments in Turkey — Save 60–70% | Plexus Alliance"
        description="Browse all dental treatments available in Turkey: implants, veneers, All-on-4, smile makeovers, orthodontics, whitening & more. Transparent pricing, doctor supervision, JCI-accredited clinics."
        keywords="dental treatments Turkey, dental implants cost Turkey, porcelain veneers Turkey, All-on-4 implants Istanbul, smile makeover Turkey"
        canonical={`${siteUrl}/treatments`}
        schema={schema}
      />
      <Navigation />

      {/* ── HERO ── */}
      <section className="relative min-h-[85vh] flex flex-col justify-center overflow-hidden pt-28 pb-20 md:pt-36 md:pb-28">
        {/* Background image */}
        <div className="absolute inset-0">
          <img
            src="https://public.readdy.ai/ai/img_res/cdfa6976f187cbbb75d3a466cb3086c5.jpg"
            alt="Luxury dental clinic"
            className="w-full h-full object-cover object-center"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-black/90 via-black/70 to-black/50" />
          <div className="absolute inset-0 bg-gradient-to-t from-[#0a0a0a] via-transparent to-black/40" />
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-10 w-full">
          <div className={`transition-all duration-1000 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
            {/* Eyebrow */}
            <div className="flex items-center gap-4 mb-8">
              <div className="w-12 h-px bg-amber-400/60" />
              <span className="text-amber-400/90 text-xs font-medium tracking-[0.3em] uppercase" style={{ fontFamily: "'Inter', sans-serif" }}>
                All Treatments
              </span>
            </div>

            {/* Headline */}
            <h1
              className="text-5xl md:text-7xl lg:text-8xl font-normal text-white leading-[1.05] mb-6 max-w-4xl"
              style={{ fontFamily: "'Playfair Display', serif", letterSpacing: '-0.01em' }}
            >
              Your smile.
              <br />
              <em className="text-amber-300 not-italic font-medium">Our expertise.</em>
            </h1>

            <p className="text-base md:text-lg text-white/50 mb-12 max-w-xl font-light leading-relaxed" style={{ fontFamily: "'Inter', sans-serif" }}>
              World-class dental care in Turkey — at a fraction of what you'd pay at home.
            </p>

            {/* Stats */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6 md:gap-10 border-t border-white/10 pt-10">
              {[
                { value: '50K+', label: 'Patients Treated' },
                { value: '98%', label: 'Satisfaction Rate' },
                { value: '70%', label: 'Average Savings' },
                { value: '8', label: 'Treatments Available' },
              ].map((s, i) => (
                <div key={i}>
                  <div className="text-3xl md:text-4xl font-normal text-amber-300 mb-1" style={{ fontFamily: "'Playfair Display', serif" }}>
                    {s.value}
                  </div>
                  <div className="text-xs text-white/30 font-medium tracking-wide uppercase" style={{ fontFamily: "'Inter', sans-serif" }}>{s.label}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ── TREATMENTS GRID ── */}
      <section className="py-20 md:py-28 bg-[#0f0f0f]">
        <div className="max-w-7xl mx-auto px-6 md:px-10">
          {/* Section header */}
          <div className="mb-14 md:mb-20">
            <div className="flex items-center gap-4 mb-6">
              <div className="w-12 h-px bg-amber-400/60" />
              <span className="text-amber-400/80 text-xs font-medium tracking-[0.3em] uppercase" style={{ fontFamily: "'Inter', sans-serif" }}>
                What we offer
              </span>
            </div>
            <h2
              className="text-4xl md:text-6xl font-normal text-white leading-tight max-w-3xl"
              style={{ fontFamily: "'Playfair Display', serif", letterSpacing: '-0.01em' }}
            >
              Pick your treatment.
              <br />
              <em className="text-amber-300 not-italic">We handle the rest.</em>
            </h2>
          </div>

          {/* Grid */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-px bg-white/5 border border-white/5">
            {treatments.map((tr) => (
              <button
                key={tr.id}
                id={tr.id}
                onClick={() => window.REACT_APP_NAVIGATE(`/book-treatment?treatment=${tr.id}`)}
                className="group relative text-left p-6 md:p-8 bg-[#0f0f0f] hover:bg-[#161616] transition-all duration-300 cursor-pointer scroll-mt-32"
              >
                {tr.tag && (
                  <span className="absolute top-4 right-4 text-[10px] font-semibold text-amber-400 bg-amber-400/10 border border-amber-400/20 px-2 py-0.5" style={{ fontFamily: "'Inter', sans-serif" }}>
                    {tr.tag}
                  </span>
                )}
                <div className="w-10 h-10 flex items-center justify-center mb-5">
                  <i className={`${tr.icon} text-2xl text-white/20 group-hover:text-amber-400 transition-colors duration-300`} />
                </div>
                <h3 className="text-sm md:text-base font-semibold text-white/80 leading-snug mb-2" style={{ fontFamily: "'Inter', sans-serif" }}>
                  {tr.title}
                </h3>
                <p className="text-xs text-white/30 mb-1" style={{ fontFamily: "'Inter', sans-serif" }}>{tr.stat}</p>
                <p className="text-xs text-white/30 mb-4" style={{ fontFamily: "'Inter', sans-serif" }}>
                  <i className="ri-time-line mr-1" />
                  {tr.duration}
                </p>
                <div className="flex items-center gap-1 text-amber-400 text-xs font-medium opacity-0 group-hover:opacity-100 transition-opacity duration-300" style={{ fontFamily: "'Inter', sans-serif" }}>
                  <span>Book now</span>
                  <i className="ri-arrow-right-line text-xs" />
                </div>
              </button>
            ))}
          </div>

          {/* Bottom note */}
          <div className="mt-10 md:mt-14 flex flex-col md:flex-row items-start md:items-center justify-between gap-6 border-t border-white/5 pt-8">
            <p className="text-white/30 text-sm font-light max-w-md" style={{ fontFamily: "'Inter', sans-serif" }}>
              Every treatment is supervised by a qualified doctor — from first scan to final result.
            </p>
            <button
              onClick={() => window.REACT_APP_NAVIGATE('/book-treatment')}
              className="flex-shrink-0 bg-amber-400 hover:bg-amber-300 text-black px-7 py-3 font-semibold text-sm tracking-wide transition-all hover:scale-105 cursor-pointer whitespace-nowrap"
              style={{ fontFamily: "'Inter', sans-serif" }}
            >
              Book a Free Consultation
              <i className="ri-arrow-right-line ml-2" />
            </button>
          </div>
        </div>
      </section>

      {/* ── WHY TURKEY ── */}
      <section className="py-20 md:py-28 bg-[#0a0a0a]">
        <div className="max-w-7xl mx-auto px-6 md:px-10">
          <div className="mb-14 md:mb-20">
            <div className="flex items-center gap-4 mb-6">
              <div className="w-12 h-px bg-amber-400/60" />
              <span className="text-amber-400/80 text-xs font-medium tracking-[0.3em] uppercase" style={{ fontFamily: "'Inter', sans-serif" }}>
                Why Turkey
              </span>
            </div>
            <h2
              className="text-4xl md:text-6xl font-normal text-white leading-tight"
              style={{ fontFamily: "'Playfair Display', serif", letterSpacing: '-0.01em' }}
            >
              The same quality.
              <br />
              <em className="text-amber-300 not-italic">A fraction of the cost.</em>
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-px bg-white/5 border border-white/5">
            {whyPoints.map((pt, i) => (
              <div key={i} className="bg-[#0a0a0a] p-8 md:p-10 hover:bg-[#111111] transition-colors duration-300 group cursor-default">
                <div className="w-10 h-10 flex items-center justify-center mb-6">
                  <i className={`${pt.icon} text-2xl text-amber-400/60 group-hover:text-amber-400 transition-colors duration-300`} />
                </div>
                <h3 className="text-base md:text-lg font-medium text-white/70 leading-snug" style={{ fontFamily: "'Inter', sans-serif" }}>
                  {pt.headline}
                </h3>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── CTA ── */}
      <section className="relative overflow-hidden min-h-[60vh] flex items-center justify-center">
        <div className="absolute inset-0">
          <img
            src="https://readdy.ai/api/search-image?query=beautiful%20woman%20confident%20smile%20after%20dental%20treatment%20Istanbul%20Turkey%20luxury%20clinic%20warm%20golden%20light%20elegant%20portrait%20moody%20atmospheric%20photography%20dark%20background%20premium%20aesthetic%20editorial&width=1440&height=800&seq=treatments-cta-dark-01&orientation=landscape"
            alt="Beautiful smile result"
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/70 via-black/60 to-black/80" />
        </div>

        <div className="relative z-10 max-w-4xl mx-auto px-6 md:px-10 text-center py-24 md:py-32">
          <div className="flex items-center justify-center gap-4 mb-8">
            <div className="w-12 h-px bg-amber-400/60" />
            <span className="text-amber-400/80 text-xs font-medium tracking-[0.3em] uppercase" style={{ fontFamily: "'Inter', sans-serif" }}>
              Your journey starts here
            </span>
            <div className="w-12 h-px bg-amber-400/60" />
          </div>

          <h2
            className="text-5xl md:text-7xl font-normal text-white leading-[1.05] mb-8"
            style={{ fontFamily: "'Playfair Display', serif", letterSpacing: '-0.01em' }}
          >
            I'm here.
            <br />
            <em className="text-amber-300 not-italic">Let's do this together.</em>
          </h2>

          <p className="text-white/50 text-base md:text-lg font-light mb-12 max-w-xl mx-auto leading-relaxed" style={{ fontFamily: "'Inter', sans-serif" }}>
            You deserve to feel confident. We're with you personally — every step of the way.
          </p>

          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <button
              onClick={() => window.REACT_APP_NAVIGATE('/book-treatment')}
              className="bg-amber-400 hover:bg-amber-300 text-black px-10 py-4 font-semibold text-sm tracking-wide transition-all hover:scale-105 cursor-pointer whitespace-nowrap"
              style={{ fontFamily: "'Inter', sans-serif" }}
            >
              Start My Journey
              <i className="ri-arrow-right-line ml-2" />
            </button>
            <button
              onClick={() => window.open('https://wa.me/message/OAKDCTBVIML6F1', '_blank')}
              className="border border-white/20 text-white/70 hover:text-white hover:border-white/40 px-10 py-4 font-medium text-sm tracking-wide transition-all cursor-pointer whitespace-nowrap"
              style={{ fontFamily: "'Inter', sans-serif" }}
            >
              <i className="ri-whatsapp-line mr-2" />
              Chat with Us
            </button>
          </div>

          <div className="mt-12 flex flex-wrap items-center justify-center gap-8 text-white/25 text-xs tracking-widest uppercase" style={{ fontFamily: "'Inter', sans-serif" }}>
            <span>Doctor Supervised</span>
            <span className="text-amber-400/30">·</span>
            <span>Personal Care</span>
            <span className="text-amber-400/30">·</span>
            <span>Free Consultation</span>
          </div>
        </div>
      </section>

      <Footer onBookClick={() => window.REACT_APP_NAVIGATE('/book-treatment')} />
    </div>
  );
}
